import { useEffect, useState } from "react";
import { Wallet2, Loader2, RefreshCw, Percent } from "lucide-react";
import OwnerLayout from "@/components/owner/OwnerLayout";
import apiClient from "@/lib/api";
import { formatRupiah } from "@/components/property/roomMeta";
import { BOOKING_STATUS_LABELS, BOOKING_STATUS_STYLES } from "@/components/booking/bookingMeta";

export default function OwnerFinancePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = () => {
    setLoading(true); setError(false);
    apiClient.get("/owner/finance")
      .then((r) => setData(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const t = data?.totals;

  return (
    <OwnerLayout testid="owner-finance-page">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Keuangan</h1>
          <p className="mt-2 text-sm text-muted-foreground">Ringkasan pendapatan kamar, komisi platform, dan estimasi yang menjadi hak Anda. Deposit dihitung terpisah.</p>
        </div>
        <div className="flex items-center gap-2">
          {data && (
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold bg-primary/10 text-primary border border-primary/20" data-testid="owner-finance-rate">
              <Percent size={13} /> Komisi Avasa {data.active_commission_rate}%
            </span>
          )}
          <button onClick={load} data-testid="owner-finance-refresh"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-primary border border-border hover:bg-secondary/60 transition-colors">
            <RefreshCw size={14} /> Muat Ulang
          </button>
        </div>
      </div>

      {loading ? (
        <div className="mt-10 grid place-items-center py-16"><Loader2 className="animate-spin text-primary" size={28} /></div>
      ) : error ? (
        <div className="mt-8 rounded-2xl border border-destructive/30 bg-destructive/10 p-8 text-center" data-testid="owner-finance-error">
          <p className="text-sm text-destructive">Gagal memuat data keuangan.</p>
          <button onClick={load} className="mt-3 text-sm font-semibold text-primary hover:underline">Coba lagi</button>
        </div>
      ) : (
        <>
          <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="owner-finance-summary">
            <Card label="Gross Room Revenue" value={formatRupiah(t.gross_room)} />
            <Card label="Diskon / Promo" value={`-${formatRupiah(t.discount)}`} tone="emerald" />
            <Card label="Net Room Revenue" value={formatRupiah(t.net_room)} />
            <Card label="Room Dibayar" value={formatRupiah(t.room_paid)} />
            <Card label="Komisi Platform" value={`-${formatRupiah(t.platform_commission)}`} tone="rose" />
            <Card label="Owner Room Payable" value={formatRupiah(t.owner_room_payable)} tone="primary" />
            <Card label="Deposit Dibayar" value={formatRupiah(t.deposit_paid)} />
            <Card label="Total Owner Payable" value={formatRupiah(t.owner_total_payable)} tone="primary" highlight />
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Owner Payable = pendapatan kamar yang sudah dibayar − komisi platform + deposit. Komisi & pengembalian (refund) dihitung dari pembayaran yang benar-benar berhasil. Belum ada pencairan otomatis pada tahap ini.
          </p>

          <h2 className="mt-8 font-heading text-lg font-semibold">Rincian per Booking</h2>
          {t.booking_count === 0 ? (
            <div className="mt-3 rounded-2xl border border-dashed border-border bg-secondary/30 p-10 text-center" data-testid="owner-finance-empty">
              <span className="grid place-items-center w-12 h-12 rounded-2xl bg-secondary text-primary mx-auto mb-3"><Wallet2 size={22} /></span>
              <p className="text-sm text-muted-foreground">Belum ada pendapatan kamar. Data muncul setelah ada pembayaran kamar yang berhasil.</p>
            </div>
          ) : (
            <div className="mt-3 overflow-x-auto rounded-2xl border border-border" data-testid="owner-finance-table">
              <table className="w-full text-sm">
                <thead className="bg-secondary/50 text-muted-foreground">
                  <tr className="text-left">
                    <th className="px-3 py-3 font-semibold">Booking / Property</th>
                    <th className="px-3 py-3 font-semibold text-right">Gross</th>
                    <th className="px-3 py-3 font-semibold text-right">Diskon</th>
                    <th className="px-3 py-3 font-semibold text-right">Net</th>
                    <th className="px-3 py-3 font-semibold text-right">Room Dibayar</th>
                    <th className="px-3 py-3 font-semibold text-right">Komisi</th>
                    <th className="px-3 py-3 font-semibold text-right">Owner Room</th>
                    <th className="px-3 py-3 font-semibold text-right">Deposit</th>
                  </tr>
                </thead>
                <tbody>
                  {data.bookings.map((b, i) => (
                    <tr key={b.booking_id} data-testid={`owner-finance-row-${i}`} className="border-t border-border">
                      <td className="px-3 py-3">
                        <div className="font-medium">{b.property_name || "Property"}</div>
                        <div className="flex items-center gap-2 mt-0.5">
                          <span className="font-mono text-xs text-muted-foreground">{b.booking_id.slice(0, 8)}</span>
                          {b.promo_code && <span className="font-mono text-[10px] text-primary bg-primary/10 rounded px-1.5">{b.promo_code}</span>}
                          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${BOOKING_STATUS_STYLES[b.booking_status] || ""}`}>{BOOKING_STATUS_LABELS[b.booking_status] || b.booking_status}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-right">{formatRupiah(b.gross_room)}</td>
                      <td className="px-3 py-3 text-right text-emerald-600">{b.discount ? `-${formatRupiah(b.discount)}` : "-"}</td>
                      <td className="px-3 py-3 text-right">{formatRupiah(b.net_room)}</td>
                      <td className="px-3 py-3 text-right">{formatRupiah(b.room_paid)}</td>
                      <td className="px-3 py-3 text-right text-rose-600">-{formatRupiah(b.platform_commission)}</td>
                      <td className="px-3 py-3 text-right font-semibold text-primary">{formatRupiah(b.owner_room_payable)}</td>
                      <td className="px-3 py-3 text-right">{formatRupiah(b.deposit_paid)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </OwnerLayout>
  );
}

function Card({ label, value, tone, highlight }) {
  const toneCls = tone === "primary" ? "text-primary" : tone === "rose" ? "text-rose-600" : tone === "emerald" ? "text-emerald-600" : "text-foreground";
  return (
    <div className={`rounded-2xl border p-4 ${highlight ? "border-primary/40 bg-primary/5" : "border-border bg-card"}`}>
      <p className="text-[11px] font-bold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={`mt-1 font-heading text-lg font-bold ${toneCls}`}>{value}</p>
    </div>
  );
}
